<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
// "Clientes" — layout Lais 1:1 com /leads real (capturado em DOM autenticado):
//   Title "Clientes" + helper "Tenha a visão completa e organizada dos clientes que entraram em contato com a Lais."
//   Filtros, "Exibindo N cliente", tabela 5-col (Cliente | Contato | Operação | Canal | CRM)
//   Bottom action bar: Baixar selecionados | Enviar selecionados | Programar Remarketing
//   Status 3-níveis: Não pronto | Qualificado | Pronto para atendimento
import { ref, computed, onMounted, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LaisCrmContactsAPI from 'dashboard/api/laisCrmContacts';
import LaisDisparosAPI from 'dashboard/api/laisDisparos';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useEscClose } from 'dashboard/composables/useEscClose';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { buildRealEstateCapabilityContext } from 'dashboard/helper/permissionsHelper';
import { REAL_ESTATE_SURFACES } from 'dashboard/constants/permissions';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisFilterButton from 'dashboard/components-next/LaisFilterButton.vue';
import LaisActionBar from 'dashboard/components-next/LaisActionBar.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisMetricStrip from 'dashboard/components-next/LaisMetricStrip.vue';
import FinanceiroAPI from 'dashboard/api/financeiro';
import CrmFiltersDrawer from '../components/CrmFiltersDrawer.vue';
import LeadFilterChipGroup from '../components/LeadFilterChipGroup.vue';
import LeadsList from '../components/LeadsList.vue';
import LeadsListState from '../components/LeadsListState.vue';
import LeadQuickView from '../components/LeadQuickView.vue';
import LeadSelectionSummary from '../components/LeadSelectionSummary.vue';
import PipelineIndex from './PipelineIndex.vue';
import LeadReferralsIndex from './LeadReferralsIndex.vue';
import CrmTasksIndex from './CrmTasksIndex.vue';
import BlockedContactsConsole from './BlockedContactsConsole.vue';
import InquiriesIndex from '../../laisInquiries/pages/InquiriesIndex.vue';
import { LAIS_AGENT_PERMISSIONS } from '../../laisRouteHelpers';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const routeString = value => (Array.isArray(value) ? value[0] : value);
const currentAccount = useMapGetter('getCurrentAccount');
const meta = ref({});
const capabilityError = ref(null);
const capabilitiesContext = computed(() =>
  buildRealEstateCapabilityContext({
    surface: REAL_ESTATE_SURFACES.LEADS_INDEX,
    account: currentAccount.value,
    meta: meta.value,
    error: capabilityError.value,
  })
);
const capabilityFor = action =>
  capabilitiesContext.value.actions[action] || {
    visible: false,
    disabled: true,
    allowed: false,
  };
const canSeeReferrals = computed(() => capabilityFor('referrals').visible);
const canSeeLeads = computed(() => capabilityFor('view').visible);
const canSeePipeline = computed(() => capabilityFor('pipeline').visible);
const canUseLeadFilters = computed(() => capabilityFor('filters').visible);
const canSendLeadCrm = computed(() => capabilityFor('send_crm').visible);
const canSendLeadCrmAction = computed(() => capabilityFor('send_crm').allowed);
const canManageLeadRecords = computed(() => capabilityFor('edit').visible);
const canManageLeadRecordsAction = computed(
  () => capabilityFor('edit').allowed
);
const canDeleteLeadRecords = computed(() => capabilityFor('delete').visible);
const canDeleteLeadRecordsAction = computed(
  () => capabilityFor('delete').allowed
);
const canUseRemarketingAction = computed(
  () => capabilityFor('remarketing').allowed
);
const canUseLeadQuickView = computed(() => capabilityFor('quick_view').visible);
const canSeeMessages = computed(() =>
  LAIS_AGENT_PERMISSIONS.some(permission =>
    capabilitiesContext.value.permissions.includes(permission)
  )
);
const canSeeTasks = computed(() => canSeeLeads.value);
const leadsCapabilityNotice = computed(() => {
  const { state } = capabilitiesContext.value;

  if (state.permissionDenied) {
    return {
      tone: 'danger',
      title: 'Acesso restrito',
      description:
        'Seu perfil não possui a capability canônica para visualizar clientes nesta conta.',
    };
  }
  if (state.stale) {
    return {
      tone: 'warning',
      title: 'Dados desatualizados',
      description:
        'A carteira foi marcada como stale; filtros e leitura seguem disponíveis, mas ações de escrita ficam bloqueadas.',
    };
  }
  if (state.partial) {
    return {
      tone: 'warning',
      title: 'Resultado parcial',
      description:
        'A resposta de clientes está parcial. Ações de CRM, edição e remarketing ficam bloqueadas até nova carga completa.',
    };
  }

  return null;
});

const VIEW_TABS = [
  { value: 'leads', label: 'Clientes' },
  { value: 'funil', label: 'Funil' },
  { value: 'mensagens', label: 'Mensagens' },
  { value: 'tarefas', label: 'Tarefas' },
  { value: 'bloqueados', label: 'Bloqueados' },
  { value: 'repasses', label: 'Repasses' },
];
const VIEW_TAB_VALUES = VIEW_TABS.map(tab => tab.value);
const visibleViewTabs = computed(() =>
  VIEW_TABS.filter(tab => {
    if (tab.value === 'leads') {
      return (
        canSeeLeads.value || capabilitiesContext.value.state.permissionDenied
      );
    }
    if (tab.value === 'funil') return canSeePipeline.value;
    if (tab.value === 'mensagens') return canSeeMessages.value;
    if (tab.value === 'tarefas') return canSeeTasks.value;
    if (tab.value === 'bloqueados') return canSeeLeads.value;
    if (tab.value === 'repasses') return canSeeReferrals.value;
    return false;
  })
);
const isViewAvailable = view =>
  visibleViewTabs.value.some(tab => tab.value === view);
const firstAvailableView = () => visibleViewTabs.value[0]?.value || 'leads';
const normalizedView = value => {
  const view = VIEW_TAB_VALUES.includes(value) ? value : 'leads';
  return isViewAvailable(view) ? view : firstAvailableView();
};
const activeView = ref(normalizedView(routeString(route.query.view)));
const viewTabId = view => `clientes-${view}-tab`;
const viewPanelId = view => `clientes-${view}-panel`;
const activeViewQuery = view => {
  const query = { ...route.query };
  if (view === 'leads') delete query.view;
  else query.view = view;
  return query;
};
const querySignature = query =>
  Object.entries(query)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([key, value]) => {
      const values = Array.isArray(value) ? value : [value];
      return values
        .filter(item => item !== undefined && item !== null && item !== '')
        .map(item => `${key}:${item}`);
    })
    .join('|');
const handleRouteSyncError = () => {
  useAlert('Não foi possível atualizar a URL dos filtros.');
};
const syncActiveViewQuery = view => {
  const query = activeViewQuery(view);
  if (querySignature(route.query) === querySignature(query)) return;
  router.replace({ query }).catch(handleRouteSyncError);
};
const setActiveView = view => {
  const next = normalizedView(view);
  if (next !== activeView.value) activeView.value = next;
  syncActiveViewQuery(next);
};
const focusViewTab = view => {
  if (typeof document === 'undefined') return;
  document.getElementById(viewTabId(view))?.focus();
};
const scheduleViewTabFocus = view => {
  const scheduler =
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : callback => callback();
  scheduler(() => focusViewTab(view));
};
const moveViewTabFocus = delta => {
  const currentIndex = Math.max(
    0,
    visibleViewTabs.value.findIndex(tab => tab.value === activeView.value)
  );
  const nextIndex =
    (currentIndex + delta + visibleViewTabs.value.length) %
    visibleViewTabs.value.length;
  const nextView = visibleViewTabs.value[nextIndex]?.value;
  if (!nextView) return;
  setActiveView(nextView);
  scheduleViewTabFocus(nextView);
};
const handleViewTabsKeydown = event => {
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault();
      moveViewTabFocus(1);
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault();
      moveViewTabFocus(-1);
      break;
    case 'Home': {
      event.preventDefault();
      const nextView = visibleViewTabs.value[0]?.value;
      if (nextView) {
        setActiveView(nextView);
        scheduleViewTabFocus(nextView);
      }
      break;
    }
    case 'End': {
      event.preventDefault();
      const nextView = visibleViewTabs.value.at(-1)?.value;
      if (nextView) {
        setActiveView(nextView);
        scheduleViewTabFocus(nextView);
      }
      break;
    }
    default:
      break;
  }
};

const routeNumber = (value, fallback) => {
  const parsed = Number(routeString(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const normalizedQualification = value =>
  ['todos', 'nao_qualificado', 'qualificado', 'pronto'].includes(value)
    ? value
    : 'todos';
const normalizedCrmStatus = value =>
  [
    'todos',
    'com_crm',
    'sem_crm',
    'queued',
    'processing',
    'synced',
    'failed',
  ].includes(value)
    ? value
    : 'todos';
const DEFAULT_ACTIVITY_DAYS = '180';
const normalizedActivityDays = value =>
  ['30', '60', '90', '120', '180', '360'].includes(String(value || ''))
    ? String(value)
    : DEFAULT_ACTIVITY_DAYS;
const normalizedFinishedVisits = value =>
  [
    'ONE',
    'TWO',
    'THREE',
    'FOUR',
    'FIVE_OR_MORE',
    '1',
    '2',
    '3',
    '4',
    '5+',
  ].includes(String(value || ''))
    ? String(value)
    : '';
const normalizedScheduleMoment = value =>
  [
    'NEW',
    'WITH_VISIT_TODAY',
    'WITH_VISIT_SCHEDULED',
    'WITHOUT_VISIT_SCHEDULED',
    'WITH_SALE_OFFER',
    'WITHOUT_SALE_OFFER',
  ].includes(String(value || ''))
    ? String(value)
    : '';
const normalizedClientType = value =>
  ['PFA', 'NOT_PFA'].includes(String(value || '')) ? String(value) : '';
const normalizedCharacteristics = value =>
  ['AGENT_LEAD_REFERRAL'].includes(String(value || '')) ? String(value) : '';
const normalizedOrderBy = value =>
  [
    'recent_activity',
    'oldest_activity',
    'alphabetical',
    'alphabetical_desc',
  ].includes(String(value || ''))
    ? String(value)
    : '';
const normalizedPerPage = value => {
  const parsed = routeNumber(value, 10);
  return [10, 25, 50].includes(parsed) ? parsed : 10;
};
const normalizedDatePeriod = value =>
  ['today', 'last_7_days', 'last_30_days', 'custom'].includes(
    String(value || '')
  )
    ? String(value)
    : '';
const toISODate = date => date.toISOString().slice(0, 10);
const REMARKETING_MIN = 30;
const REMARKETING_MAX = 60;
const CRM_BATCH_MAX = 500;
const remarketingRangeMessage = `Para seguir com a solicitação de remarketing você precisa selecionar, no mínimo, ${REMARKETING_MIN} leads e, no máximo, ${REMARKETING_MAX}.`;
const remarketingMaxMessage = `Selecione até ${REMARKETING_MAX} leads para enviar`;
const remarketingConcurrentLimitMessage =
  'Você atingiu o limite de solicitações simultâneas de Remarketing. Aguarde o próximo disparo.';
const remarketingReviewSuccessMessage =
  'A sua solicitação foi enviada e será revisada na data programada. A imobiliária será notificada no momento de disparo.';
const remarketingDailyConflictMessage =
  'Já existe uma solicitação para essa data';
const remarketingMetaErrorMessage =
  'A Meta está passando por uma instabilidade. Acompanhe o andamento do seu disparo em Notificações.';

const isLoading = ref(false);
const loadError = ref(false);
const leads = ref([]);
const billingUsage = ref({ percentual: 0, near_limit: false });
const page = ref(routeNumber(route.query.page, 1));
const perPage = ref(normalizedPerPage(route.query.per_page));
const qualificationFilter = ref(
  normalizedQualification(routeString(route.query.status))
);
const showFilters = ref(routeString(route.query.filters) === '1');
const leadFilters = ref({
  operacao: routeString(route.query.operacao) || '',
  canal: routeString(route.query.canal) || '',
  origem: routeString(route.query.origem) || '',
  lead_origin_id: routeString(route.query.lead_origin_id) || '',
  campanha: routeString(route.query.campanha) || '',
  crm_status: normalizedCrmStatus(routeString(route.query.crm_status)),
  date_period: normalizedDatePeriod(routeString(route.query.date_period)),
  start_date: routeString(route.query.start_date) || '',
  end_date: routeString(route.query.end_date) || '',
  activity_days: normalizedActivityDays(routeString(route.query.activity_days)),
  finished_visits_count: normalizedFinishedVisits(
    routeString(route.query.finished_visits_count)
  ),
  time_of_schedule: normalizedScheduleMoment(
    routeString(route.query.time_of_schedule || route.query.quick_filter)
  ),
  client_type: normalizedClientType(routeString(route.query.client_type)),
  characteristics: normalizedCharacteristics(
    routeString(route.query.characteristics)
  ),
  order_by: normalizedOrderBy(routeString(route.query.order_by)),
});
const selected = ref(new Set());
const showRemarketing = ref(false);
const showCrmModal = ref(false);
const crmModalMode = ref('list');
const crmLeadTarget = ref(null);
const quickViewLead = ref(null);
const showLeadEditModal = ref(false);
const editingLead = ref(null);
const deleteLeadTarget = ref(null);
const isSavingLead = ref(false);
const isDeletingLead = ref(false);
const editLeadForm = ref({
  name: '',
  email: '',
  phone_number: '',
  qualification_status: 'nao_qualificado',
  operacao: '',
  canal: '',
  origem: '',
  campanha: '',
});
const remarketingStep = ref(1);
const remarketingModo = ref('recomendada');
const remarketingCanal = ref('whatsapp');
const remarketingMensagem = ref('');
const remarketingData = ref('');
const isSendingRemarketing = ref(false);
const isSyncingCrm = ref(false);
const isExportingSelected = ref(false);
const isExportingFiltered = ref(false);
const selectedLeads = computed(() =>
  leads.value.filter(lead => selected.value.has(lead.id))
);
const selectedBlockedLeads = computed(() =>
  selectedLeads.value.filter(lead => lead.blocked || lead.blocked_contact)
);
const selectedDeliverableLeadsCount = computed(() =>
  Math.max(selected.value.size - selectedBlockedLeads.value.length, 0)
);
const selectedCountLabel = computed(
  () =>
    `${selected.value.size} ${
      selected.value.size === 1 ? 'selecionado' : 'selecionados'
    }`
);
const isRemarketingSelectionOutOfRange = computed(
  () =>
    selected.value.size > 0 &&
    (selected.value.size < REMARKETING_MIN ||
      selected.value.size > REMARKETING_MAX)
);
const selectionHint = computed(() => {
  if (selected.value.size === 0) return '';
  if (selected.value.size < REMARKETING_MIN) {
    return `Remarketing exige de ${REMARKETING_MIN} a ${REMARKETING_MAX} clientes.`;
  }
  if (selected.value.size > REMARKETING_MAX) {
    return `Remarketing aceita até ${REMARKETING_MAX} clientes. Baixar e enviar ao CRM seguem disponíveis.`;
  }
  return `${selectedDeliverableLeadsCount.value} aptos ao disparo`;
});

const leadTabs = [
  { id: 'todos', label: 'Todos' },
  { id: 'nao_qualificado', label: 'Não pronto' },
  { id: 'qualificado', label: 'Qualificado' },
  { id: 'pronto', label: 'Pronto para atendimento' },
];

const leadStatusOptions = leadTabs.filter(tab => tab.id !== 'todos');

const canaisDisponiveis = [
  { id: '', label: 'Todos os canais' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'web', label: 'Site / Web' },
  { id: 'email', label: 'E-mail' },
  { id: 'sms', label: 'SMS' },
];

const clientTypeOptions = [
  { value: '', label: 'Todos', icon: 'i-lucide-users' },
  { value: 'PFA', label: 'PFA', icon: 'i-lucide-user-star' },
  { value: 'NOT_PFA', label: 'Não PFA', icon: 'i-lucide-user-round' },
];

const activityFilterOptions = [
  { value: '', label: 'Qualquer', icon: 'i-lucide-sparkles' },
  { value: '30', label: '30 dias' },
  { value: '60', label: '60 dias' },
  { value: '90', label: '90 dias' },
  { value: '120', label: '120 dias' },
  { value: '180', label: '180 dias' },
  { value: '360', label: '360 dias' },
];

const finishedVisitsFilterOptions = [
  { value: '', label: 'Qualquer', icon: 'i-lucide-calendar-check' },
  { value: 'ONE', label: '1 realizada' },
  { value: 'TWO', label: '2 realizadas' },
  { value: 'THREE', label: '3 realizadas' },
  { value: 'FOUR', label: '4 realizadas' },
  { value: 'FIVE_OR_MORE', label: '5+ realizadas' },
];

const scheduleMomentFilterOptions = [
  { value: '', label: 'Todos', icon: 'i-lucide-clock-3' },
  { value: 'NEW', label: 'Novos' },
  { value: 'WITH_VISIT_TODAY', label: 'Visita hoje' },
  { value: 'WITH_VISIT_SCHEDULED', label: 'Visita agendada' },
  { value: 'WITHOUT_VISIT_SCHEDULED', label: 'Sem visita agendada' },
  { value: 'WITH_SALE_OFFER', label: 'Proposta em andamento' },
  { value: 'WITHOUT_SALE_OFFER', label: 'Sem proposta' },
];

const characteristicFilterOptions = [
  { value: '', label: 'Todas', icon: 'i-lucide-tags' },
  {
    value: 'AGENT_LEAD_REFERRAL',
    label: 'Indicação de corretor',
    icon: 'i-lucide-handshake',
  },
];

const orderByOptions = [
  { value: '', label: 'Padrão', description: 'Ordenação padrão da carteira' },
  {
    value: 'recent_activity',
    label: 'Atividade recente',
    description: 'Ordenado por atividade mais recente',
  },
  {
    value: 'oldest_activity',
    label: 'Atividade antiga',
    description: 'Ordenado por atividade mais antiga',
  },
  { value: 'alphabetical', label: 'A-Z', description: 'Ordenado por nome A-Z' },
  {
    value: 'alphabetical_desc',
    label: 'Z-A',
    description: 'Ordenado por nome Z-A',
  },
];

const CRM_STATUS_META = {
  queued: { label: 'Na fila', tone: 'info' },
  processing: { label: 'Processando', tone: 'info' },
  synced: { label: 'Sincronizado', tone: 'positive' },
  failed: { label: 'Falhou', tone: 'danger' },
  skipped: { label: 'Ignorado', tone: 'neutral' },
  not_ready: { label: 'Não enviado', tone: 'neutral' },
};

const hasAdvancedFilters = computed(
  () =>
    !!leadFilters.value.operacao ||
    !!leadFilters.value.canal ||
    !!leadFilters.value.origem ||
    !!leadFilters.value.lead_origin_id ||
    !!leadFilters.value.campanha ||
    leadFilters.value.crm_status !== 'todos' ||
    !!leadFilters.value.date_period ||
    !!leadFilters.value.start_date ||
    !!leadFilters.value.end_date ||
    (!!leadFilters.value.activity_days &&
      leadFilters.value.activity_days !== DEFAULT_ACTIVITY_DAYS) ||
    !!leadFilters.value.finished_visits_count ||
    !!leadFilters.value.time_of_schedule ||
    !!leadFilters.value.client_type ||
    !!leadFilters.value.characteristics ||
    !!leadFilters.value.order_by
);

const hasAnyLeadFilter = computed(
  () => hasAdvancedFilters.value || qualificationFilter.value !== 'todos'
);

const totalLeadsCount = computed(() => meta.value.count || leads.value.length);

const activeOrderDescription = computed(
  () =>
    orderByOptions.find(option => option.value === leadFilters.value.order_by)
      ?.description || orderByOptions[0].description
);

if (hasAdvancedFilters.value) showFilters.value = true;

const dateRangeFromPreset = () => {
  const today = new Date();
  const period = leadFilters.value.date_period;

  if (period === 'today') {
    const date = toISODate(today);
    return { start_date: date, end_date: date };
  }

  if (period === 'last_7_days' || period === 'last_30_days') {
    const days = period === 'last_7_days' ? 7 : 30;
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - (days - 1));
    return {
      start_date: toISODate(startDate),
      end_date: toISODate(today),
    };
  }

  return {
    start_date: leadFilters.value.start_date,
    end_date: leadFilters.value.end_date,
  };
};

const planUsagePercent = computed(() =>
  Number(billingUsage.value?.percentual || 0)
);
const planUsageAtLimit = computed(() => planUsagePercent.value >= 100);
const showPlanUsageBanner = computed(
  () => planUsageAtLimit.value || Boolean(billingUsage.value?.near_limit)
);

const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );

const shouldSyncLeadRouteState = computed(
  () => activeView.value === 'leads' && !routeString(route.query.view)
);
const visibleViewSignature = computed(() =>
  visibleViewTabs.value.map(tab => tab.value).join('|')
);
const hasViewPermissionContext = computed(
  () =>
    capabilitiesContext.value.permissions.length > 0 ||
    Boolean(capabilityError.value) ||
    Object.keys(meta.value).length > 0
);
watch(
  [() => route.query.view, visibleViewSignature],
  () => {
    const requestedView = routeString(route.query.view);
    const requestedKnown = VIEW_TAB_VALUES.includes(requestedView);
    const waitingForPermissionContext =
      requestedView &&
      requestedKnown &&
      !isViewAvailable(requestedView) &&
      !hasViewPermissionContext.value;

    if (waitingForPermissionContext) return;

    const next = normalizedView(requestedView);
    if (next !== activeView.value) activeView.value = next;

    if (
      (requestedView && requestedView !== next) ||
      (!requestedView && next !== 'leads')
    ) {
      syncActiveViewQuery(next);
    }
  },
  { immediate: true }
);

const syncLeadsRouteState = () => {
  if (!shouldSyncLeadRouteState.value) return;

  const nextQuery = compactQuery({
    ...route.query,
    status:
      qualificationFilter.value === 'todos'
        ? undefined
        : qualificationFilter.value,
    page: page.value > 1 ? String(page.value) : undefined,
    per_page: perPage.value !== 10 ? String(perPage.value) : undefined,
    filters: showFilters.value || hasAdvancedFilters.value ? '1' : undefined,
    operacao: leadFilters.value.operacao || undefined,
    canal: leadFilters.value.canal || undefined,
    origem: leadFilters.value.origem || undefined,
    lead_origin_id: leadFilters.value.lead_origin_id || undefined,
    campanha: leadFilters.value.campanha || undefined,
    crm_status:
      leadFilters.value.crm_status === 'todos'
        ? undefined
        : leadFilters.value.crm_status,
    date_period: leadFilters.value.date_period || undefined,
    start_date:
      leadFilters.value.date_period === 'custom'
        ? leadFilters.value.start_date || undefined
        : undefined,
    end_date:
      leadFilters.value.date_period === 'custom'
        ? leadFilters.value.end_date || undefined
        : undefined,
    activity_days:
      leadFilters.value.activity_days === DEFAULT_ACTIVITY_DAYS
        ? undefined
        : leadFilters.value.activity_days || undefined,
    finished_visits_count: leadFilters.value.finished_visits_count || undefined,
    time_of_schedule: leadFilters.value.time_of_schedule || undefined,
    client_type: leadFilters.value.client_type || undefined,
    characteristics: leadFilters.value.characteristics || undefined,
    order_by: leadFilters.value.order_by || undefined,
  });

  if (querySignature(route.query) === querySignature(nextQuery)) return;

  router.replace({ query: nextQuery }).catch(handleRouteSyncError);
};

const toggleFilters = () => {
  showFilters.value = !showFilters.value;
  syncLeadsRouteState();
};

const leadQueryPayload = ({ paginated = false } = {}) => {
  const payload = {
    include_contact_inboxes: 'false',
  };
  const dateRange = dateRangeFromPreset();
  if (paginated) {
    payload.page = String(page.value);
    payload.per_page = String(perPage.value);
  }
  if (leadFilters.value.order_by) payload.order_by = leadFilters.value.order_by;
  else if (paginated) payload.sort = 'name';
  if (qualificationFilter.value !== 'todos') {
    payload.qualification_status = qualificationFilter.value;
  }
  [
    'operacao',
    'canal',
    'origem',
    'lead_origin_id',
    'campanha',
    'activity_days',
    'finished_visits_count',
    'time_of_schedule',
    'client_type',
    'characteristics',
  ].forEach(key => {
    if (leadFilters.value[key]) payload[key] = leadFilters.value[key];
  });
  if (leadFilters.value.crm_status !== 'todos') {
    payload.crm_status = leadFilters.value.crm_status;
  }
  if (dateRange.start_date) payload.start_date = dateRange.start_date;
  if (dateRange.end_date) payload.end_date = dateRange.end_date;
  return payload;
};

const carregar = async () => {
  if (!canSeeLeads.value) {
    leads.value = [];
    loadError.value = false;
    capabilityError.value = { status: 403 };
    return;
  }

  isLoading.value = true;
  syncLeadsRouteState();
  try {
    const { data } = await LaisCrmContactsAPI.list(
      leadQueryPayload({ paginated: true })
    );
    leads.value = data.payload || [];
    meta.value = data.meta || {};
    loadError.value = false;
    capabilityError.value = null;
  } catch (e) {
    loadError.value = true;
    capabilityError.value = e;
    useAlert('Não foi possível carregar os clientes');
  } finally {
    isLoading.value = false;
  }
};

onMounted(carregar);

const loadBillingUsage = async () => {
  try {
    const { data } = await FinanceiroAPI.cobranca();
    billingUsage.value = data.uso || billingUsage.value;
  } catch {
    // Consumo é informativo; falha não bloqueia a gestão de leads.
  }
};

onMounted(loadBillingUsage);

const setQualificationFilter = tab => {
  qualificationFilter.value = tab;
  page.value = 1;
  selected.value.clear();
  carregar();
};

const applyLeadFilters = () => {
  page.value = 1;
  selected.value.clear();
  carregar();
};

const resetLeadFilters = () => {
  leadFilters.value = {
    operacao: '',
    canal: '',
    origem: '',
    lead_origin_id: '',
    campanha: '',
    crm_status: 'todos',
    date_period: '',
    start_date: '',
    end_date: '',
    activity_days: DEFAULT_ACTIVITY_DAYS,
    finished_visits_count: '',
    time_of_schedule: '',
    client_type: '',
    characteristics: '',
    order_by: '',
  };
  page.value = 1;
  selected.value.clear();
  carregar();
};

const allSelected = computed(
  () =>
    leads.value.length > 0 && leads.value.every(l => selected.value.has(l.id))
);
const toggleAll = () => {
  if (allSelected.value) selected.value.clear();
  else leads.value.forEach(l => selected.value.add(l.id));
};
const toggleOne = id =>
  selected.value.has(id) ? selected.value.delete(id) : selected.value.add(id);
const clearSelection = () => {
  selected.value.clear();
  selected.value = new Set();
};

const totalPages = computed(() =>
  Math.max(
    1,
    Math.ceil((meta.value.count || leads.value.length) / perPage.value)
  )
);

const goToPreviousPage = () => {
  if (page.value <= 1) return;

  page.value -= 1;
  carregar();
};

const goToNextPage = () => {
  if (page.value >= totalPages.value) return;

  page.value += 1;
  carregar();
};

const historicoRoute = (id, query = {}) => ({
  name: 'lais_lead_historico',
  params: { accountId: accountId.value, id },
  query,
});

const verHistorico = (id, query = {}) => {
  router.push(historicoRoute(id, query));
};

const abrirTimelineOperacional = lead => {
  if (!lead?.id) return;

  verHistorico(lead.id, { tab: 'historico', event: 'crm' });
};

const abrirQuickView = lead => {
  if (!canUseLeadQuickView.value) return;

  quickViewLead.value = lead;
};

const openLeadEdit = lead => {
  if (!canManageLeadRecordsAction.value) return;

  const attrs = lead.additional_attributes || {};
  editingLead.value = lead;
  editLeadForm.value = {
    name: lead.name || '',
    email: lead.email || '',
    phone_number: lead.phone_number || '',
    qualification_status: lead.qualification_status || 'nao_qualificado',
    operacao: attrs.operacao || attrs.operacao_interesse || '',
    canal: attrs.canal || '',
    origem: attrs.origem || attrs.origem_lead || attrs.utm_source || '',
    campanha: attrs.campanha || attrs.utm_campaign || '',
  };
  showLeadEditModal.value = true;
};

const closeLeadEdit = () => {
  showLeadEditModal.value = false;
  editingLead.value = null;
};

const saveLeadEdit = async () => {
  if (!editingLead.value?.id) return;
  if (!canManageLeadRecordsAction.value) {
    useAlert('Você não possui permissão para editar este cliente.');
    return;
  }

  isSavingLead.value = true;
  try {
    await LaisCrmContactsAPI.update(editingLead.value.id, {
      name: editLeadForm.value.name,
      email: editLeadForm.value.email,
      phone_number: editLeadForm.value.phone_number,
      qualification_status: editLeadForm.value.qualification_status,
      additional_attributes: {
        operacao: editLeadForm.value.operacao || null,
        canal: editLeadForm.value.canal || null,
        origem: editLeadForm.value.origem || null,
        campanha: editLeadForm.value.campanha || null,
      },
    });
    useAlert('Cliente atualizado com sucesso.');
    closeLeadEdit();
    await carregar();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível atualizar este cliente.'
    );
  } finally {
    isSavingLead.value = false;
  }
};

const openLeadDelete = lead => {
  if (!canDeleteLeadRecordsAction.value) return;

  deleteLeadTarget.value = lead;
};

const closeLeadDelete = () => {
  deleteLeadTarget.value = null;
};

const deleteLead = async () => {
  if (!deleteLeadTarget.value?.id) return;
  if (!canDeleteLeadRecordsAction.value) {
    useAlert('Você não possui permissão para excluir este cliente.');
    return;
  }

  isDeletingLead.value = true;
  try {
    await LaisCrmContactsAPI.destroy(deleteLeadTarget.value.id);
    selected.value.delete(deleteLeadTarget.value.id);
    selected.value = new Set(selected.value);
    useAlert('Cliente excluído com sucesso.');
    closeLeadDelete();
    await carregar();
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.response?.data?.error ||
        'Não foi possível excluir este cliente.'
    );
  } finally {
    isDeletingLead.value = false;
  }
};

const abrirHistoricoQuickView = lead => {
  if (!lead?.id) return;
  quickViewLead.value = null;
  verHistorico(lead.id);
};

const abrirRepasseQuickView = lead => {
  router.push({
    name: 'lais_lead_referrals',
    params: { accountId: accountId.value },
    query: lead?.id ? { contact_id: String(lead.id) } : {},
  });
};

const agendarVisitaQuickView = lead => {
  if (!lead?.id) return;
  router.push({
    name: 'visita_new',
    params: { accountId: accountId.value },
    query: { contact_id: String(lead.id) },
  });
};

const leadPhone = lead =>
  lead?.phone_number || lead?.phoneNumber || lead?.contact?.phone_number || '';

const normalizedLeadPhone = lead => leadPhone(lead).replace(/\D+/g, '');

const mensagemQuickView = lead => {
  const phone = normalizedLeadPhone(lead);
  if (!phone) {
    useAlert('Telefone não informado para este cliente.');
    return;
  }
  window.open(`https://wa.me/55${phone.replace(/^55/, '')}`, '_blank');
};

const ligarQuickView = lead => {
  const phone = normalizedLeadPhone(lead);
  if (!phone) {
    useAlert('Telefone não informado para este cliente.');
    return;
  }
  window.open(`tel:${phone}`, '_blank');
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const exportLeads = async ({ contactIds = [], filenamePrefix, loadingRef }) => {
  loadingRef.value = true;
  try {
    const { data } = await LaisCrmContactsAPI.exportLeads({
      ...leadQueryPayload(),
      contact_ids: contactIds,
    });
    downloadBlob(data, `${filenamePrefix}-${Date.now()}.csv`);
  } catch (error) {
    useAlert('Não foi possível baixar o arquivo de clientes.');
  } finally {
    loadingRef.value = false;
  }
};

const baixarSelecionados = () => {
  if (!capabilityFor('export_selected').allowed) return;

  exportLeads({
    contactIds: Array.from(selected.value),
    filenamePrefix: 'clientes-selecionados',
    loadingRef: isExportingSelected,
  });
};

const baixarResultadoFiltrado = () => {
  if (!capabilityFor('export_filtered').allowed) return;

  exportLeads({
    filenamePrefix: 'clientes-filtrados',
    loadingRef: isExportingFiltered,
  });
};

const crmSummary = lead =>
  lead.crm_sync || lead.additional_attributes?.crm_sync || {};
const crmStatus = lead => crmSummary(lead).status || 'not_ready';
const crmStatusMeta = lead =>
  CRM_STATUS_META[crmStatus(lead)] || CRM_STATUS_META.not_ready;

const abrirEnvioCrm = () => {
  if (!canSendLeadCrmAction.value) {
    useAlert('Não é possível enviar lead ao CRM com as permissões atuais.');
    return;
  }
  if (selected.value.size === 0) {
    useAlert('Selecione ao menos um cliente para enviar ao sistema conectado.');
    return;
  }
  crmModalMode.value = 'list';
  crmLeadTarget.value = null;
  showCrmModal.value = true;
};

const abrirEnvioCrmIndividual = leadId => {
  if (!canSendLeadCrmAction.value) {
    useAlert('Não é possível enviar lead ao CRM com as permissões atuais.');
    return;
  }

  crmModalMode.value = 'unique';
  crmLeadTarget.value = leads.value.find(lead => lead.id === leadId) || {
    id: leadId,
    name: 'este lead',
  };
  showCrmModal.value = true;
};

const crmContactIds = computed(() =>
  crmModalMode.value === 'unique' && crmLeadTarget.value?.id
    ? [crmLeadTarget.value.id]
    : Array.from(selected.value)
);
const crmSelectionTooLong = computed(
  () =>
    crmModalMode.value === 'list' && crmContactIds.value.length > CRM_BATCH_MAX
);
const crmModalTitle = computed(() =>
  crmModalMode.value === 'unique' ? 'Enviar lead ao CRM' : 'Enviar leads ao CRM'
);
const crmModalDescription = computed(() => {
  if (crmModalMode.value === 'unique') {
    return `Enviar ${crmLeadTarget.value?.name || 'este lead'} ao CRM?`;
  }

  return 'Envie para o CRM vários leads de uma vez.';
});
const closeCrmModal = () => {
  showCrmModal.value = false;
  crmLeadTarget.value = null;
};

const copiarLinkLead = async leadId => {
  try {
    const link = new URL(
      frontendURL(`accounts/${accountId.value}/history/${leadId}`),
      window.location.origin
    );
    await navigator.clipboard.writeText(link.toString());
    useAlert(t('LEADS.REFERRALS.SERVICE_LINK_COPY_SUCCESS'));
  } catch (error) {
    useAlert(t('LEADS.REFERRALS.COPY_ERROR'));
  }
};

const enviarSelecionados = async () => {
  if (!canSendLeadCrmAction.value) {
    useAlert('Não é possível enviar lead ao CRM com as permissões atuais.');
    return;
  }

  if (crmSelectionTooLong.value) {
    useAlert('Esta lista é muito longa para ser processada.');
    return;
  }

  const contactIds = crmContactIds.value;
  if (contactIds.length === 0) {
    useAlert('Selecione ao menos um lead para enviar ao CRM.');
    return;
  }

  isSyncingCrm.value = true;
  try {
    const { data } = await LaisCrmContactsAPI.syncToCrm(contactIds);
    useAlert(
      `${data.queued || contactIds.length} ${contactIds.length === 1 ? 'lead enviado ao CRM' : 'leads enviados ao CRM'}`
    );
    closeCrmModal();
    if (crmModalMode.value === 'list') clearSelection();
    await carregar();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não é possível enviar lead ao CRM'
    );
  } finally {
    isSyncingCrm.value = false;
  }
};

const abrirComoFunciona = () => {
  if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
    window.Intercom('showNewMessage', 'Como funciona o Remarketing?');
  } else {
    window.open('/lais/suporte', '_blank', 'noopener');
  }
};

const abrirRemarketing = () => {
  if (!canUseRemarketingAction.value) {
    useAlert('Você não possui permissão para programar remarketing.');
    return;
  }

  if (planUsageAtLimit.value) {
    useAlert(
      'Para usar essa funcionalidade, que tal dar um upgrade no seu plano?'
    );
    return;
  }
  if (selected.value.size < REMARKETING_MIN) {
    useAlert(remarketingRangeMessage);
    return;
  }
  if (selected.value.size > REMARKETING_MAX) {
    useAlert(remarketingMaxMessage);
    return;
  }
  remarketingStep.value = 1;
  remarketingModo.value = 'recomendada';
  remarketingCanal.value = 'whatsapp';
  remarketingMensagem.value = '';
  remarketingData.value = '';
  showRemarketing.value = true;
};

const proximoStep = () => {
  if (remarketingStep.value === 1 && selected.value.size < REMARKETING_MIN) {
    useAlert(remarketingRangeMessage);
    return;
  }
  if (remarketingStep.value === 1 && selected.value.size > REMARKETING_MAX) {
    useAlert(remarketingMaxMessage);
    return;
  }
  if (remarketingStep.value >= 3) return;
  remarketingStep.value += 1;
};

const stepAnterior = () => {
  if (remarketingStep.value <= 1) return;
  remarketingStep.value -= 1;
};

useEscClose(showRemarketing, () => {
  showRemarketing.value = false;
});
useEscClose(showCrmModal, () => {
  closeCrmModal();
});
useEscClose(showLeadEditModal, closeLeadEdit);

const remarketingErrorMessage = error => {
  const message = (
    error.response?.data?.errors?.[0] ||
    error.response?.data?.error ||
    ''
  ).toString();
  const normalized = message.toLowerCase();

  if (normalized.includes('simult')) {
    return remarketingConcurrentLimitMessage;
  }
  if (
    normalized.includes('um disparo por dia') ||
    normalized.includes('solicitação para essa data') ||
    normalized.includes('solicitacao para essa data')
  ) {
    return remarketingDailyConflictMessage;
  }
  if (normalized.includes('meta')) {
    return remarketingMetaErrorMessage;
  }

  return message || 'Não foi possível enviar o remarketing para revisão';
};

const confirmarRemarketing = async () => {
  if (!canUseRemarketingAction.value) {
    useAlert('Você não possui permissão para programar remarketing.');
    return;
  }

  isSendingRemarketing.value = true;
  try {
    const payload = {
      lais_disparo: {
        titulo: `Remarketing ${new Date().toLocaleDateString('pt-BR')}`,
        canal: remarketingCanal.value,
        mensagem: remarketingMensagem.value || 'Mensagem recomendada',
        agendado_para: remarketingData.value,
        filtros: {
          lead_ids: Array.from(selected.value),
        },
      },
    };
    await LaisDisparosAPI.create(payload);
    useAlert(remarketingReviewSuccessMessage);
    showRemarketing.value = false;
    selected.value.clear();
  } catch (e) {
    useAlert(remarketingErrorMessage(e));
  } finally {
    isSendingRemarketing.value = false;
  }
};

const actions = computed(() =>
  [
    {
      capability: 'export_selected',
      label: 'Baixar selecionados',
      icon: 'i-lucide-download',
      disabled:
        selected.value.size === 0 ||
        isExportingSelected.value ||
        !capabilityFor('export_selected').allowed,
      onClick: baixarSelecionados,
    },
    {
      capability: 'export_filtered',
      label: 'Baixar resultado filtrado',
      icon: 'i-lucide-file-down',
      disabled:
        isExportingFiltered.value ||
        isLoading.value ||
        !capabilityFor('export_filtered').allowed,
      onClick: baixarResultadoFiltrado,
    },
    {
      capability: 'send_crm',
      label: 'Enviar selecionados',
      icon: 'i-lucide-send',
      disabled: selected.value.size === 0 || !canSendLeadCrmAction.value,
      onClick: abrirEnvioCrm,
    },
    {
      capability: 'remarketing',
      label: 'Programar Remarketing',
      icon: 'i-lucide-megaphone',
      disabled:
        selected.value.size === 0 ||
        isRemarketingSelectionOutOfRange.value ||
        !canUseRemarketingAction.value,
      onClick: abrirRemarketing,
    },
  ].filter(action => capabilityFor(action.capability).visible)
);

const STATUS_META = {
  nao_qualificado: { label: 'Não pronto', tone: 'warning' },
  qualificado: { label: 'Qualificado', tone: 'info' },
  pronto: { label: 'Pronto para atendimento', tone: 'positive' },
};

const formatLeadDate = value => {
  if (!value) return '';
  const date = new Date(
    Number(value) < 1_000_000_000_000 ? Number(value) * 1000 : value
  );
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR');
};

const leadStats = lead => {
  const crm = lead.lais_crm || lead.additional_attributes?.lais_crm || {};
  const stats = crm.stats || crm.activity_stats || {};
  return {
    visits: lead.finished_visits_count ?? stats.finished_visits_count ?? 0,
    offers: lead.active_offers_count ?? stats.active_offers_count ?? 0,
    lastActivity: formatLeadDate(lead.last_activity_at || crm.last_activity_at),
  };
};

// Presentational summary tiles for the metric strip. Derived only from data
// already loaded (no extra requests): the account-wide total comes from the
// paginated meta.count; the stage breakdown reflects the leads on this page.
const stageCount = status =>
  leads.value.filter(lead => lead.qualification_status === status).length;
const metricItems = computed(() => {
  const items = [
    {
      id: 'total',
      label: 'Clientes',
      value: totalLeadsCount.value,
      tone: 'info',
      icon: 'i-lucide-users',
      helper: hasAnyLeadFilter.value ? 'no filtro atual' : 'na carteira',
    },
    {
      id: 'pronto',
      label: 'Prontos para atendimento',
      value: stageCount('pronto'),
      tone: 'positive',
      icon: 'i-lucide-user-check',
      helper: 'nesta página',
    },
    {
      id: 'qualificado',
      label: 'Qualificados',
      value: stageCount('qualificado'),
      tone: 'info',
      icon: 'i-lucide-user-round-check',
      helper: 'nesta página',
    },
    {
      id: 'nao_pronto',
      label: 'Não prontos',
      value: stageCount('nao_qualificado'),
      tone: 'warning',
      icon: 'i-lucide-user-round-search',
      helper: 'nesta página',
    },
  ];
  if (selected.value.size > 0) {
    items.push({
      id: 'selecionados',
      label: 'Selecionados',
      value: selected.value.size,
      tone: 'neutral',
      icon: 'i-lucide-check-check',
      helper: selectionHint.value,
    });
  }
  return items;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-white font-redHatText md:overflow-hidden"
  >
    <LaisHeader
      title="Clientes"
      helper="Tenha a visão completa e organizada dos clientes que entraram em contato com a Lais."
      sticky
    >
      <template #actions>
        <LaisFilterButton
          v-if="canUseLeadFilters"
          :active="
            showFilters || qualificationFilter !== 'todos' || hasAdvancedFilters
          "
          @click="toggleFilters"
        />
      </template>
    </LaisHeader>

    <div
      v-if="visibleViewTabs.length"
      class="border-b border-lais-border-rule px-4 sm:px-6 lg:px-10"
    >
      <div
        role="tablist"
        aria-label="Áreas de Clientes"
        class="flex items-center gap-6 overflow-x-auto no-scrollbar"
        @keydown="handleViewTabsKeydown"
      >
        <button
          v-for="tab in visibleViewTabs"
          :id="viewTabId(tab.value)"
          :key="tab.value"
          type="button"
          role="tab"
          class="relative inline-flex h-12 shrink-0 items-center gap-2 text-[14px] font-semibold transition-colors"
          :class="
            activeView === tab.value
              ? 'text-lais-primary'
              : 'text-lais-text-muted hover:text-lais-text-body'
          "
          :aria-selected="activeView === tab.value ? 'true' : 'false'"
          :aria-controls="viewPanelId(tab.value)"
          :tabindex="activeView === tab.value ? 0 : -1"
          @click="setActiveView(tab.value)"
        >
          <span>{{ tab.label }}</span>
          <span
            v-if="activeView === tab.value"
            class="absolute bottom-0 left-0 right-0 h-[3px] rounded-t bg-lais-primary"
          />
        </button>
      </div>
    </div>

    <div
      v-if="activeView === 'funil'"
      :id="viewPanelId('funil')"
      role="tabpanel"
      :aria-labelledby="viewTabId('funil')"
      tabindex="0"
      class="min-h-0 flex-1 overflow-hidden outline-none focus-visible:shadow-lais-focus max-md:flex-none max-md:overflow-visible"
    >
      <PipelineIndex />
    </div>
    <div
      v-else-if="activeView === 'repasses'"
      :id="viewPanelId('repasses')"
      role="tabpanel"
      :aria-labelledby="viewTabId('repasses')"
      tabindex="0"
      class="min-h-0 flex-1 overflow-hidden outline-none focus-visible:shadow-lais-focus max-md:flex-none max-md:overflow-visible"
    >
      <LeadReferralsIndex />
    </div>
    <div
      v-else-if="activeView === 'tarefas'"
      :id="viewPanelId('tarefas')"
      role="tabpanel"
      :aria-labelledby="viewTabId('tarefas')"
      tabindex="0"
      class="min-h-0 flex-1 overflow-auto bg-lais-bg outline-none focus-visible:shadow-lais-focus max-md:flex-none max-md:overflow-visible"
    >
      <CrmTasksIndex />
    </div>
    <div
      v-else-if="activeView === 'bloqueados'"
      :id="viewPanelId('bloqueados')"
      role="tabpanel"
      :aria-labelledby="viewTabId('bloqueados')"
      tabindex="0"
      class="min-h-0 flex-1 overflow-hidden bg-lais-bg outline-none focus-visible:shadow-lais-focus max-md:flex-none max-md:overflow-visible"
    >
      <BlockedContactsConsole />
    </div>
    <div
      v-else-if="activeView === 'mensagens'"
      :id="viewPanelId('mensagens')"
      role="tabpanel"
      :aria-labelledby="viewTabId('mensagens')"
      tabindex="0"
      class="min-h-0 flex-1 overflow-hidden bg-lais-bg outline-none focus-visible:shadow-lais-focus max-md:flex-none max-md:overflow-visible"
    >
      <InquiriesIndex />
    </div>

    <div
      v-else
      :id="viewPanelId('leads')"
      role="tabpanel"
      :aria-labelledby="viewTabId('leads')"
      tabindex="0"
      class="flex min-h-0 flex-1 flex-col overflow-hidden outline-none focus-visible:shadow-lais-focus max-md:flex-none max-md:overflow-visible"
    >
      <div
        v-if="leadsCapabilityNotice"
        class="mx-4 mt-4 rounded-lais-sm border px-4 py-3 text-[13px] leading-relaxed sm:mx-6 lg:mx-10"
        :class="
          leadsCapabilityNotice.tone === 'danger'
            ? 'border-lais-danger/40 bg-lais-danger-50 text-lais-danger'
            : 'border-amber-200 bg-amber-50 text-amber-800'
        "
      >
        <p class="font-semibold">{{ leadsCapabilityNotice.title }}</p>
        <p class="mt-1">{{ leadsCapabilityNotice.description }}</p>
      </div>

      <LeadsListState
        v-if="capabilitiesContext.state.permissionDenied"
        class="mx-4 mt-4 sm:mx-6 lg:mx-10"
        title="Acesso restrito"
        description="Seu perfil não possui permissão para visualizar clientes nesta conta."
        icon="i-lucide-lock"
        critical
      />

      <template v-else>
        <div class="px-4 pt-4 sm:px-6 lg:px-10">
          <LaisMetricStrip
            :items="metricItems"
            :loading="isLoading && leads.length === 0"
            aria-label="Resumo de clientes"
          />
        </div>

        <div
          v-if="showPlanUsageBanner"
          class="mx-4 mt-4 flex flex-col gap-2 rounded-lais border border-[#f0c36d] bg-[#fff8e6] px-4 py-3 sm:mx-6 sm:flex-row sm:items-center sm:justify-between lg:mx-10"
        >
          <div>
            <p class="text-[14px] font-semibold text-[#92670c]">
              <template v-if="planUsageAtLimit">
                Você atingiu 100% do consumo do plano
              </template>
              <template v-else>
                Atualize seu plano e economize com mais leads atendidos
              </template>
            </p>
            <p class="text-[12px] text-[#92670c]">
              Para usar essa funcionalidade, que tal dar um upgrade no seu
              plano?
            </p>
          </div>
          <button
            type="button"
            class="rounded-lais bg-[#92670c] px-4 py-2 text-center text-[13px] font-semibold text-white"
            @click="
              router.push({
                name: 'planos_index',
                params: { accountId },
              })
            "
          >
            Atualizar plano
          </button>
        </div>

        <div class="border-b border-[#e5e7eb] px-4 sm:px-6 lg:px-10">
          <div class="flex gap-3 overflow-x-auto py-3 no-scrollbar">
            <button
              v-for="tab in leadTabs"
              :key="tab.id"
              type="button"
              class="whitespace-nowrap rounded-full border px-3 py-2 text-[13px] font-semibold transition-colors"
              :class="
                qualificationFilter === tab.id
                  ? 'border-lais-primary bg-[#f0e8fa] text-lais-primary'
                  : 'border-[#e5e7eb] text-lais-text-body hover:border-lais-primary/40'
              "
              @click="setQualificationFilter(tab.id)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <CrmFiltersDrawer
          :show="showFilters"
          @close="
            showFilters = false;
            syncLeadsRouteState();
          "
          @clear="resetLeadFilters"
          @apply="applyLeadFilters"
        >
          <div class="flex flex-col gap-1">
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Operação
            </label>
            <select
              v-model="leadFilters.operacao"
              class="h-10 w-full sm:w-40 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option value="">Todas</option>
              <option value="venda">Venda</option>
              <option value="aluguel">Aluguel</option>
              <option value="temporada">Temporada</option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Canal de recebimento
            </label>
            <select
              v-model="leadFilters.canal"
              class="h-10 w-full sm:w-44 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option
                v-for="canal in canaisDisponiveis"
                :key="canal.id || 'todos-canais'"
                :value="canal.id"
              >
                {{ canal.label }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Origem / mídia
            </label>
            <input
              v-model="leadFilters.origem"
              type="text"
              placeholder="Ex.: meta ads"
              class="h-10 w-full sm:w-44 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
              @keyup.enter="applyLeadFilters"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Anúncio de origem
            </label>
            <input
              v-model="leadFilters.lead_origin_id"
              type="text"
              placeholder="Buscar pelo ID do anúncio Meta Ads"
              class="h-10 w-full sm:w-56 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
              @keyup.enter="applyLeadFilters"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Campanha
            </label>
            <input
              v-model="leadFilters.campanha"
              type="text"
              placeholder="Buscar por nome da campanha Meta Ads"
              class="h-10 w-full sm:w-44 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
              @keyup.enter="applyLeadFilters"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Status CRM
            </label>
            <select
              v-model="leadFilters.crm_status"
              class="h-10 w-full sm:w-40 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option value="todos">Todos</option>
              <option value="com_crm">Com CRM</option>
              <option value="sem_crm">Sem CRM</option>
              <option value="queued">Na fila</option>
              <option value="processing">Processando</option>
              <option value="synced">Sincronizado</option>
              <option value="failed">Falhou</option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Data do primeiro contato
            </label>
            <select
              v-model="leadFilters.date_period"
              class="h-10 w-full sm:w-48 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option value="">Todos</option>
              <option value="today">Hoje</option>
              <option value="last_7_days">Últimos 7 dias</option>
              <option value="last_30_days">Últimos 30 dias</option>
              <option value="custom">Período personalizado</option>
            </select>
          </div>

          <div
            v-if="leadFilters.date_period === 'custom'"
            class="flex flex-col gap-1"
          >
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              De
            </label>
            <input
              v-model="leadFilters.start_date"
              type="date"
              class="h-10 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
            />
          </div>

          <div
            v-if="leadFilters.date_period === 'custom'"
            class="flex flex-col gap-1"
          >
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Até
            </label>
            <input
              v-model="leadFilters.end_date"
              type="date"
              class="h-10 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
            />
          </div>

          <LeadFilterChipGroup
            v-model="leadFilters.activity_days"
            label="Atividade"
            :options="activityFilterOptions"
          />

          <LeadFilterChipGroup
            v-model="leadFilters.finished_visits_count"
            label="Visitas"
            :options="finishedVisitsFilterOptions"
          />

          <LeadFilterChipGroup
            v-model="leadFilters.time_of_schedule"
            label="Momento"
            :options="scheduleMomentFilterOptions"
          />

          <LeadFilterChipGroup
            v-model="leadFilters.client_type"
            label="Tipo"
            :options="clientTypeOptions"
          />

          <LeadFilterChipGroup
            v-model="leadFilters.characteristics"
            label="Característica"
            :options="characteristicFilterOptions"
          />

          <div class="flex flex-col gap-1">
            <label
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-body"
            >
              Ordenar
            </label>
            <select
              v-model="leadFilters.order_by"
              class="h-10 w-full sm:w-48 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option value="">Padrão</option>
              <option value="recent_activity">Atividade recente</option>
              <option value="oldest_activity">Atividade antiga</option>
              <option value="alphabetical">A-Z</option>
              <option value="alphabetical_desc">Z-A</option>
            </select>
          </div>
        </CrmFiltersDrawer>

        <LeadSelectionSummary
          :total-leads-count="totalLeadsCount"
          :active-order-description="activeOrderDescription"
          :order-by="leadFilters.order_by"
          :order-by-options="orderByOptions"
          :selected-count="selected.size"
          @clear-selection="clearSelection"
          @order-change="
            value => ((leadFilters.order_by = value), applyLeadFilters())
          "
        />

        <div
          class="min-h-0 flex-1 overflow-auto px-4 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:px-6 sm:pb-4 lg:px-10 max-md:flex-none max-md:overflow-visible"
        >
          <LeadsList
            :is-loading="isLoading"
            :load-error="loadError"
            :leads="leads"
            :selected="selected"
            :all-selected="allSelected"
            :selected-count="selected.size"
            :selected-count-label="selectedCountLabel"
            :selection-hint="selectionHint"
            :is-remarketing-selection-out-of-range="
              isRemarketingSelectionOutOfRange
            "
            :has-any-lead-filter="hasAnyLeadFilter"
            :can-send-lead-crm="canSendLeadCrm"
            :can-send-lead-crm-action="canSendLeadCrmAction"
            :can-use-lead-quick-view="canUseLeadQuickView"
            :can-manage-lead-records="canManageLeadRecords"
            :can-manage-lead-records-action="canManageLeadRecordsAction"
            :can-delete-lead-records="canDeleteLeadRecords"
            :can-delete-lead-records-action="canDeleteLeadRecordsAction"
            :status-meta="STATUS_META"
            :crm-summary="crmSummary"
            :crm-status-meta="crmStatusMeta"
            :lead-stats="leadStats"
            @toggle-all="toggleAll"
            @toggle-one="toggleOne"
            @retry="carregar"
            @reset-filters="resetLeadFilters"
            @view-history="verHistorico"
            @open-crm="abrirEnvioCrmIndividual"
            @open-timeline="abrirTimelineOperacional"
            @open-quick-view="abrirQuickView"
            @copy-link="copiarLinkLead"
            @edit-lead="openLeadEdit"
            @delete-lead="openLeadDelete"
          />
        </div>

        <div
          class="flex flex-col gap-3 border-t border-[#e5e7eb] bg-white px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10"
        >
          <div
            class="flex flex-wrap items-center gap-2 text-[13px] text-lais-text-body"
          >
            <select
              v-model.number="perPage"
              class="h-8 px-2 border border-[#e5e7eb] rounded-lais-sm text-lais-text focus:outline-none focus:border-lais-primary"
              @change="
                page = 1;
                carregar();
              "
            >
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </select>
            <span>Itens por página</span>
          </div>
          <div
            class="flex flex-wrap items-center gap-3 text-[13px] text-lais-text-body"
          >
            <span>Página {{ page }} de {{ totalPages }}</span>
            <button
              class="size-8 grid place-items-center border border-[#e5e7eb] rounded-lais-sm hover:border-lais-primary hover:text-lais-primary disabled:opacity-40"
              :disabled="page === 1"
              aria-label="Página anterior"
              @click="goToPreviousPage"
            >
              <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
            </button>
            <button
              class="size-8 grid place-items-center border border-[#e5e7eb] rounded-lais-sm hover:border-lais-primary hover:text-lais-primary disabled:opacity-40"
              :disabled="page >= totalPages"
              aria-label="Próxima página"
              @click="goToNextPage"
            >
              <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <LaisModal
          :show="showLeadEditModal"
          title="Editar cliente"
          max-width-class="max-w-[620px]"
          @close="closeLeadEdit"
        >
          <div class="grid gap-4">
            <div class="grid gap-4 sm:grid-cols-2">
              <label
                class="flex flex-col gap-1 text-[13px] font-semibold text-lais-text"
              >
                Nome
                <input
                  v-model="editLeadForm.name"
                  type="text"
                  class="h-10 rounded-lais border border-[#e5e7eb] px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
              <label
                class="flex flex-col gap-1 text-[13px] font-semibold text-lais-text"
              >
                Status
                <select
                  v-model="editLeadForm.qualification_status"
                  class="h-10 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                >
                  <option
                    v-for="status in leadStatusOptions"
                    :key="status.id"
                    :value="status.id"
                  >
                    {{ status.label }}
                  </option>
                </select>
              </label>
              <label
                class="flex flex-col gap-1 text-[13px] font-semibold text-lais-text"
              >
                E-mail
                <input
                  v-model="editLeadForm.email"
                  type="email"
                  class="h-10 rounded-lais border border-[#e5e7eb] px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
              <label
                class="flex flex-col gap-1 text-[13px] font-semibold text-lais-text"
              >
                Telefone
                <input
                  v-model="editLeadForm.phone_number"
                  type="tel"
                  class="h-10 rounded-lais border border-[#e5e7eb] px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
            </div>

            <div
              class="grid gap-4 border-t border-[#e5e7eb] pt-4 sm:grid-cols-2"
            >
              <label
                class="flex flex-col gap-1 text-[13px] font-semibold text-lais-text"
              >
                Operação
                <select
                  v-model="editLeadForm.operacao"
                  class="h-10 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                >
                  <option value="">Não informado</option>
                  <option value="venda">Venda</option>
                  <option value="aluguel">Aluguel</option>
                  <option value="temporada">Temporada</option>
                </select>
              </label>
              <label
                class="flex flex-col gap-1 text-[13px] font-semibold text-lais-text"
              >
                Canal
                <select
                  v-model="editLeadForm.canal"
                  class="h-10 rounded-lais border border-[#e5e7eb] bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                >
                  <option
                    v-for="canal in canaisDisponiveis"
                    :key="canal.id || 'canal-edicao'"
                    :value="canal.id"
                  >
                    {{ canal.label }}
                  </option>
                </select>
              </label>
              <label
                class="flex flex-col gap-1 text-[13px] font-semibold text-lais-text"
              >
                Origem / mídia
                <input
                  v-model="editLeadForm.origem"
                  type="text"
                  class="h-10 rounded-lais border border-[#e5e7eb] px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
              <label
                class="flex flex-col gap-1 text-[13px] font-semibold text-lais-text"
              >
                Campanha
                <input
                  v-model="editLeadForm.campanha"
                  type="text"
                  class="h-10 rounded-lais border border-[#e5e7eb] px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
            </div>
          </div>

          <template #footer>
            <div class="flex items-center justify-end gap-3">
              <button
                type="button"
                class="h-10 px-4 text-[13px] font-semibold text-lais-text-body hover:text-lais-text"
                @click="closeLeadEdit"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="inline-flex h-10 items-center gap-2 rounded-lais bg-lais-primary px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#3d2966] disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isSavingLead || !canManageLeadRecordsAction"
                @click="saveLeadEdit"
              >
                <span
                  v-if="isSavingLead"
                  class="i-lucide-loader-circle size-4 animate-spin"
                  aria-hidden="true"
                />
                Salvar cliente
              </button>
            </div>
          </template>
        </LaisModal>

        <LaisConfirmModal
          :show="!!deleteLeadTarget"
          title="Excluir cliente"
          :description="`Tem certeza que deseja excluir ${deleteLeadTarget?.name || 'este cliente'}? Esta ação remove o contato da conta.`"
          confirm-text="Excluir cliente"
          :is-loading="isDeletingLead"
          @close="closeLeadDelete"
          @confirm="deleteLead"
        />

        <LaisActionBar :actions="actions">
          <template #trailing>
            <button
              type="button"
              class="text-[13px] font-semibold text-lais-primary underline decoration-lais-warning decoration-[1.5px] underline-offset-[3px] hover:decoration-lais-primary"
              @click="abrirComoFunciona"
            >
              Como funciona o Remarketing?
            </button>
          </template>
        </LaisActionBar>

        <!-- Modal Remarketing wizard (3-step: Revisão → Mensagem → Resumo) -->
        <div
          v-if="showRemarketing"
          class="fixed inset-0 bg-black/40 z-50 grid place-items-center"
          @click.self="showRemarketing = false"
        >
          <div
            class="mx-4 w-full max-w-[560px] max-h-[92vh] overflow-y-auto bg-white rounded-lais shadow-lais-pop"
          >
            <header
              class="flex items-center justify-between px-6 py-4 border-b border-[#eee5d6]"
            >
              <h3
                class="text-[18px] font-semibold text-lais-text font-redHatDisplay"
              >
                Programar Remarketing
              </h3>
              <button
                type="button"
                class="size-8 grid place-items-center rounded-lais-sm hover:bg-[#fafafa]"
                aria-label="Fechar"
                @click="showRemarketing = false"
              >
                <span
                  class="i-lucide-x size-4 text-lais-text-muted"
                  aria-hidden="true"
                />
              </button>
            </header>

            <ol
              class="flex items-center gap-2 px-6 py-3 border-b border-[#eee5d6]"
            >
              <li
                v-for="(stepLabel, i) in ['Seleção', 'Configuração', 'Resumo']"
                :key="stepLabel"
                class="flex items-center gap-2 text-[12px] font-semibold"
                :class="
                  remarketingStep >= i + 1
                    ? 'text-lais-primary'
                    : 'text-lais-text-muted'
                "
              >
                <span
                  class="size-5 grid place-items-center rounded-full text-[10px]"
                  :class="
                    remarketingStep >= i + 1
                      ? 'bg-lais-primary text-white'
                      : 'bg-lais-border-soft text-lais-text-muted'
                  "
                >
                  {{ i + 1 }}
                </span>
                {{ stepLabel }}
              </li>
            </ol>

            <div class="px-6 py-5">
              <template v-if="remarketingStep === 1">
                <p class="text-[13px] text-lais-text-body mb-2">
                  Com o Remarketing, você pode resgatar uma base de clientes
                  antiga através da Lais. Caso o disparo exceda seu pacote,
                  haverá cobrança adicional.
                </p>
                <p class="text-[13px] text-lais-text-body mb-3">
                  Selecione os <strong>leads</strong> que receberão a mensagem.
                </p>
                <div
                  v-if="selected.size === 0"
                  class="rounded-lais border border-dashed border-[#e5e7eb] bg-[#fafafa] p-4 text-[13px] text-lais-text-body"
                >
                  Retorne à listagem de leads para selecionar contatos pela Casa
                  da Lais.
                </div>
                <div
                  v-if="selectedBlockedLeads.length"
                  class="mb-3 rounded-lais border border-amber-200 bg-amber-50 p-3 text-[12px] leading-relaxed text-amber-900"
                >
                  <p class="font-semibold">
                    {{ selectedBlockedLeads.length }}
                    {{
                      selectedBlockedLeads.length === 1
                        ? 'contato bloqueado'
                        : 'contatos bloqueados'
                    }}
                    na seleção.
                  </p>
                  <p class="mt-1">
                    Bloqueios aparecem antes do disparo; estes contatos serão
                    ignorados pela política de remarketing.
                  </p>
                  <RouterLink
                    class="mt-2 inline-flex font-semibold text-lais-primary"
                    :to="{
                      name: 'lais_blocked_contacts_console',
                      params: { accountId },
                    }"
                  >
                    Revisar contatos bloqueados
                  </RouterLink>
                </div>
                <div
                  v-if="selected.size > 0"
                  class="max-h-[220px] overflow-auto border border-[#eee5d6] rounded-lais-sm"
                >
                  <ul class="divide-y divide-[#eee5d6]">
                    <li
                      v-for="l in selectedLeads"
                      :key="l.id"
                      class="flex items-center gap-3 px-3 py-2 text-[13px]"
                    >
                      <span
                        class="size-7 rounded-full bg-[#f0e8fa] text-lais-primary grid place-items-center text-[11px] font-semibold shrink-0"
                      >
                        {{ (l.name || '?').slice(0, 2).toUpperCase() }}
                      </span>
                      <span class="flex-1 truncate">{{
                        l.name || 'Cliente anônimo'
                      }}</span>
                      <span
                        v-if="l.blocked || l.blocked_contact"
                        class="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
                      >
                        Bloqueado
                      </span>
                      <span class="text-[11px] text-lais-text-muted">{{
                        l.phone_number || l.email || '—'
                      }}</span>
                    </li>
                  </ul>
                </div>
                <p class="text-[11px] text-lais-text-muted mt-2">
                  Total:
                  <strong class="text-lais-primary">{{ selected.size }}</strong>
                  {{ selected.size === 1 ? 'cliente' : 'clientes' }}.
                  <span
                    v-if="
                      selected.size < REMARKETING_MIN ||
                      selected.size > REMARKETING_MAX
                    "
                    class="text-lais-danger"
                  >
                    {{ remarketingRangeMessage }}
                  </span>
                </p>
              </template>

              <template v-else-if="remarketingStep === 2">
                <label
                  class="block text-[12px] font-semibold text-lais-text mb-2"
                >
                  Para qual canal a Lais deve encaminhar os leads aquecidos?
                </label>
                <p class="mb-3 text-[12px] text-lais-text-body">
                  Escolha o <strong>canal</strong> de preferência para receber
                  os leads qualificados.
                </p>
                <div class="grid grid-cols-3 gap-2 mb-4">
                  <button
                    v-for="canal in [
                      { id: 'whatsapp', label: 'WhatsApp' },
                      { id: 'email', label: 'E-mail' },
                      { id: 'sms', label: 'SMS' },
                    ]"
                    :key="canal.id"
                    type="button"
                    class="h-10 px-2 text-[12px] font-semibold rounded-lais border transition-colors"
                    :class="
                      remarketingCanal === canal.id
                        ? 'bg-[#f0e8fa] border-lais-primary text-lais-primary'
                        : 'bg-white border-[#e5e7eb] text-lais-text-body hover:border-lais-primary'
                    "
                    @click="remarketingCanal = canal.id"
                  >
                    {{ canal.label }}
                  </button>
                </div>
                <label
                  class="block text-[12px] font-semibold text-lais-text mb-2"
                >
                  Defina a <strong>mensagem</strong> que a Lais enviará para
                  base selecionada.
                </label>
                <div class="grid grid-cols-3 gap-2 mb-4">
                  <button
                    v-for="m in [
                      { id: 'recomendada', label: 'Mensagem recomendada' },
                      { id: 'lancamentos', label: 'Mensagem para lançamentos' },
                      { id: 'personalizada', label: 'Mensagem personalizada' },
                    ]"
                    :key="m.id"
                    type="button"
                    class="h-10 px-2 text-[12px] font-semibold rounded-lais border transition-colors"
                    :class="
                      remarketingModo === m.id
                        ? 'bg-[#f0e8fa] border-lais-primary text-lais-primary'
                        : 'bg-white border-[#e5e7eb] text-lais-text-body hover:border-lais-primary'
                    "
                    @click="remarketingModo = m.id"
                  >
                    {{ m.label }}
                  </button>
                </div>
                <label
                  class="block text-[12px] font-semibold text-lais-text mb-2"
                >
                  Mensagem
                </label>
                <textarea
                  v-model="remarketingMensagem"
                  rows="4"
                  class="w-full px-3 py-2.5 text-[14px] text-lais-text bg-white border border-[#e5e7eb] rounded-lais-sm focus:outline-none focus:border-lais-primary placeholder:text-lais-text-muted"
                  :placeholder="
                    remarketingModo === 'personalizada'
                      ? 'Configure a mensagem que desejar.'
                      : 'A mensagem recomendada será usada.'
                  "
                  :disabled="remarketingModo !== 'personalizada'"
                />
                <label
                  class="block text-[12px] font-semibold text-lais-text mt-4 mb-2"
                >
                  Escolha a <strong>data</strong> em que as mensagens serão
                  disparadas pela Lais.
                </label>
                <input
                  v-model="remarketingData"
                  type="datetime-local"
                  class="w-full px-3 py-2.5 text-[14px] text-lais-text bg-white border border-[#e5e7eb] rounded-lais-sm focus:outline-none focus:border-lais-primary"
                />
                <p class="text-[11px] text-lais-text-muted mt-2">
                  É possível agendar apenas um disparo por dia.
                </p>
              </template>

              <template v-else>
                <p class="text-[13px] text-lais-text-body mb-4">
                  Tudo certo! Envie a solicitação para revisão da Lais antes do
                  disparo.
                </p>
                <dl class="grid gap-2 text-[13px]">
                  <div class="flex justify-between">
                    <dt class="text-lais-text-muted">Clientes</dt>
                    <dd class="font-semibold text-lais-text">
                      {{ selected.size }}
                    </dd>
                  </div>
                  <div
                    v-if="selectedBlockedLeads.length"
                    class="flex justify-between"
                  >
                    <dt class="text-lais-text-muted">Bloqueados</dt>
                    <dd class="font-semibold text-amber-700">
                      {{ selectedBlockedLeads.length }}
                    </dd>
                  </div>
                  <div
                    v-if="selectedBlockedLeads.length"
                    class="flex justify-between"
                  >
                    <dt class="text-lais-text-muted">Aptos ao disparo</dt>
                    <dd class="font-semibold text-lais-text">
                      {{ selectedDeliverableLeadsCount }}
                    </dd>
                  </div>
                  <div class="flex justify-between">
                    <dt class="text-lais-text-muted">Canal</dt>
                    <dd class="font-semibold text-lais-text uppercase">
                      {{ remarketingCanal }}
                    </dd>
                  </div>
                  <div class="flex justify-between">
                    <dt class="text-lais-text-muted">Tipo de mensagem</dt>
                    <dd class="font-semibold text-lais-text capitalize">
                      {{ remarketingModo }}
                    </dd>
                  </div>
                  <div class="flex justify-between">
                    <dt class="text-lais-text-muted">Data</dt>
                    <dd class="font-semibold text-lais-text">
                      {{
                        remarketingData
                          ? new Date(remarketingData).toLocaleString('pt-BR')
                          : 'Imediato'
                      }}
                    </dd>
                  </div>
                </dl>
                <div
                  class="bg-[#fffbf0] border border-lais-warning/30 p-3 rounded-lais text-[12px] text-[#5a4717] mt-4"
                >
                  A sua solicitação será revisada na data programada. A
                  imobiliária será notificada no momento de disparo.
                </div>
              </template>
            </div>

            <footer
              class="flex items-center justify-between px-6 py-4 border-t border-[#eee5d6]"
            >
              <button
                type="button"
                class="h-10 px-4 text-[13px] font-semibold text-lais-text-body hover:text-lais-text"
                @click="
                  remarketingStep > 1
                    ? stepAnterior()
                    : (showRemarketing = false)
                "
              >
                {{ remarketingStep > 1 ? 'Voltar' : 'Cancelar' }}
              </button>
              <button
                v-if="remarketingStep < 3"
                type="button"
                class="h-10 px-4 text-[13px] font-semibold text-white bg-lais-primary rounded-lais hover:bg-[#3d2966] disabled:opacity-50"
                :disabled="
                  !canUseRemarketingAction ||
                  (remarketingStep === 1 &&
                    (selected.size < REMARKETING_MIN ||
                      selected.size > REMARKETING_MAX))
                "
                @click="proximoStep"
              >
                Próximo
              </button>
              <button
                v-else
                type="button"
                class="inline-flex items-center gap-2 h-10 px-4 text-[13px] font-semibold text-white bg-lais-primary rounded-lais hover:bg-[#3d2966] disabled:opacity-50"
                :disabled="isSendingRemarketing || !canUseRemarketingAction"
                @click="confirmarRemarketing"
              >
                <span class="i-lucide-check size-4" aria-hidden="true" />
                Enviar para revisão
              </button>
            </footer>
          </div>
        </div>

        <LaisConfirmModal
          :show="showCrmModal"
          :title="crmModalTitle"
          :description="crmModalDescription"
          confirm-text="Confirmar"
          confirm-variant="primary"
          :is-loading="isSyncingCrm"
          @close="closeCrmModal"
          @confirm="enviarSelecionados"
        >
          <p class="mt-2 text-[12px] text-lais-text-body">
            <template v-if="crmModalMode === 'list'">
              A Lais vai sincronizar {{ crmContactIds.length }} leads
              selecionados com o CRM desta conta.
            </template>
            <template v-else>
              Confirme para enviar este lead ao CRM configurado nesta conta.
            </template>
          </p>
          <p
            v-if="crmSelectionTooLong"
            class="mt-3 rounded-lais-sm border border-[#f1c9cf] bg-[#fff5f6] px-3 py-2 text-[12px] font-semibold text-[#b42318]"
          >
            Esta lista é muito longa para ser processada.
          </p>
        </LaisConfirmModal>

        <LeadQuickView
          :show="!!quickViewLead"
          :lead="quickViewLead"
          :syncing-crm="isSyncingCrm"
          @close="quickViewLead = null"
          @open-history="abrirHistoricoQuickView"
          @create-referral="abrirRepasseQuickView"
          @schedule-visit="agendarVisitaQuickView"
          @message="mensagemQuickView"
          @call="ligarQuickView"
          @sync-crm="abrirEnvioCrmIndividual"
        />
      </template>
    </div>
  </section>
</template>
